import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import colors from '../styles/colors';
import api from '../services/api';

const STATUS_OK = 'ok';
const STATUS_ERROR = 'error';
const STATUS_LOADING = 'loading';

const serviceRows = [
  { label: 'AI-Vis Prompt Tracking', description: 'Prompt monitoring & analytics pipeline' },
  { label: 'Reddit Dashboard',        description: 'Reddit data sync & community tracking' },
];

function StatusDot({ status }) {
  const color =
    status === STATUS_OK      ? '#16a34a' :
    status === STATUS_ERROR   ? '#dc2626' :
                                colors.text.gray;
  const glow =
    status === STATUS_OK      ? 'rgba(22,163,74,0.2)' :
    status === STATUS_ERROR   ? 'rgba(220,38,38,0.2)' :
                                'rgba(170,170,170,0.2)';
  return (
    <span
      style={{
        display: 'inline-block',
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        background: color,
        boxShadow: `0 0 0 4px ${glow}`,
        flexShrink: 0,
      }}
    />
  );
}

function StatusBadge({ status }) {
  const cfg =
    status === STATUS_OK    ? { bg: '#f0fdf4', border: '#bbf7d0', color: '#15803d', label: 'Operational' } :
    status === STATUS_ERROR ? { bg: '#fef2f2', border: '#fecaca', color: '#dc2626', label: 'Disrupted'   } :
                              { bg: colors.bg.lightLavender, border: colors.border.lightNavy, color: colors.text.muted, label: 'Checking…' };
  return (
    <span
      style={{
        fontSize: '12px',
        fontWeight: '600',
        padding: '3px 10px',
        borderRadius: '20px',
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        color: cfg.color,
        letterSpacing: '0.03em',
      }}
    >
      {cfg.label}
    </span>
  );
}

function Status() {
  const [status, setStatus]   = useState(STATUS_LOADING);
  const [apiData, setApiData] = useState(null);

  useEffect(() => {
    api
      .get('/')
      .then((res) => {
        setApiData(res.data);
        setStatus(res.data?.status === 'ok' ? STATUS_OK : STATUS_ERROR);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          setApiData({ message: 'API is protected (X-API-KEY required)' });
          setStatus(STATUS_OK);
          return;
        }
        setApiData({ message: 'API unreachable' });
        setStatus(STATUS_ERROR);
      });
  }, []);

  const bannerBg =
    status === STATUS_OK    ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' :
    status === STATUS_ERROR ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' :
    `linear-gradient(135deg, ${colors.bg.primaryPurple} 0%, ${colors.bg.darkNavy} 100%)`;

  const overallLabel =
    status === STATUS_OK    ? 'All Systems Operational' :
    status === STATUS_ERROR ? 'Service Disruption Detected' :
                              'Checking system status…';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: colors.bg.offWhite }}>

      {/* Header */}
      <header
        style={{
          background: colors.bg.white,
          padding: '16px 0',
          borderBottom: `1px solid ${colors.border.lightNavy}`,
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <div
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Link to="/">
            <img src="/visibilitylabs.svg" alt="Visibility Labs" style={{ height: '36px', width: 'auto' }} />
          </Link>
          <nav style={{ display: 'flex', alignItems: 'center', gap: '28px' }}>
            <Link to="/" style={{ color: colors.text.muted, fontSize: '14px', fontWeight: '500', textDecoration: 'none' }}>
              Home
            </Link>
          </nav>
        </div>
      </header>

      {/* Banner */}
      <section style={{ background: bannerBg, padding: '48px 0 56px', textAlign: 'center', transition: 'background 0.4s ease' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 24px' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              background: 'rgba(255,255,255,0.12)',
              padding: '8px 20px',
              borderRadius: '40px',
              marginBottom: '20px',
            }}
          >
            <StatusDot status={status} />
            <span style={{ fontSize: '15px', fontWeight: '600', color: colors.text.white }}>
              {overallLabel}
            </span>
          </div>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
            Real-time health of the Visibility Labs platform
          </p>
        </div>
      </section>

      {/* Main */}
      <main style={{ flex: 1, padding: '48px 0 64px' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 24px' }}>

          {/* Services card */}
          <div
            style={{
              background: colors.bg.white,
              borderRadius: '16px',
              border: `1px solid ${colors.border.lightNavy}`,
              boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
              overflow: 'hidden',
              marginBottom: '24px',
            }}
          >
            <div
              style={{
                padding: '20px 28px',
                borderBottom: `1px solid ${colors.border.lightNavy}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text.darkNavy }}>Services</span>
              <StatusBadge status={status} />
            </div>

            {serviceRows.map((row, i) => (
              <div
                key={row.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '18px 28px',
                  borderBottom: i < serviceRows.length - 1 ? `1px solid ${colors.border.lightNavy}` : 'none',
                  gap: '16px',
                }}
              >
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text.darkNavy, marginBottom: '2px' }}>
                    {row.label}
                  </div>
                  <div style={{ fontSize: '12px', color: colors.text.muted }}>{row.description}</div>
                </div>
                <StatusBadge status={status} />
              </div>
            ))}
          </div>

          {/* API details card */}
          <div
            style={{
              background: colors.bg.white,
              borderRadius: '16px',
              border: `1px solid ${colors.border.lightNavy}`,
              boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '20px 28px',
                borderBottom: `1px solid ${colors.border.lightNavy}`,
              }}
            >
              <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text.darkNavy }}>API Details</span>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '18px 28px',
                borderBottom: `1px solid ${colors.border.lightNavy}`,
              }}
            >
              <span style={{ fontSize: '14px', color: colors.text.muted, fontWeight: '500' }}>Connection</span>
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: status === STATUS_OK ? '#16a34a' : status === STATUS_ERROR ? '#dc2626' : colors.text.muted,
                }}
              >
                {status === STATUS_LOADING ? 'Checking…' : status === STATUS_OK ? 'Connected' : 'Disconnected'}
              </span>
            </div>

            {apiData?.message && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '18px 28px',
                  borderBottom: apiData?.version ? `1px solid ${colors.border.lightNavy}` : 'none',
                  gap: '24px',
                }}
              >
                <span style={{ fontSize: '14px', color: colors.text.muted, fontWeight: '500', flexShrink: 0 }}>Message</span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text.darkNavy, textAlign: 'right' }}>
                  {apiData.message}
                </span>
              </div>
            )}

            {apiData?.version && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '18px 28px',
                }}
              >
                <span style={{ fontSize: '14px', color: colors.text.muted, fontWeight: '500' }}>API Version</span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text.darkNavy }}>{apiData.version}</span>
              </div>
            )}
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer style={{ background: colors.bg.darkNavy, padding: '28px 0' }}>
        <div
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>
            Visibility Labs &copy; {new Date().getFullYear()}. All rights reserved.
          </p>
          <div style={{ display: 'flex', gap: '24px' }}>
            <a href="https://visibilitylabs.ai" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>
              visibilitylabs.ai
            </a>
            <Link to="/" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>
              Home
            </Link>
          </div>
        </div>
      </footer>

    </div>
  );
}

export default Status;
